import { useCallback, useEffect, useId, useState } from "react";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { Empty, ErrorBanner } from "../../../components/Shell";
import { useT } from "../../../i18n";
import { CATALOG_TYPES, Conflict, ValidationError } from "../api/opsApi";
import type { OpsApi } from "../api/opsApi";
import type { CatalogItem, Issue, Json } from "../types";
import { fmtDate } from "./ConfigTab";
import { parseDefinition } from "./ModesTab";

/**
 * FS-15-02 카탈로그 관리 — SCR v0.6 FS-15-02(전용 와이어 없음 — sfs-15 요약 칸의 유형 칩 · '배포') · SR-CAT-01.
 *  - 유형 탭(이모트 · 문구 · 스티커 · 아바타 · 프리팹 · 피코 반응 · 힌트 규칙) · 항목 목록(키 · 버전 · 활성)
 *  - i18n 편집(ko · en) · payload(JSON) · 저장(초안) · 배포(키당 활성 1) · 끄기. 배포된 버전을 고치면 다음 버전 초안
 *  - '＋ 새 항목'(키 ^[a-z0-9-]{1,40}$)
 * 상태: 초안 · 배포됨. 학생 화면의 문구·스티커는 모두 여기 키에서 온다(자유 텍스트 0 — 입력은 운영자만).
 */
const KEY_RE = /^[a-z0-9-]{1,40}$/;

interface Edit {
  key: string;
  version: number;
  ko: string;
  en: string;
  payload: string;
  isNew: boolean;
  basePublished: boolean;
}

export function CatalogTab({ api }: { api: OpsApi }) {
  const t = useT();
  const id = useId();
  const [type, setType] = useState<string>("sticker");
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [edit, setEdit] = useState<Edit | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (ty: string) => {
      try {
        const all = await api.catalog();
        const c: Record<string, number> = {};
        for (const it of all) if (it.enabled) c[it.type] = (c[it.type] ?? 0) + 1;
        setCounts(c);
        setItems(all.filter((i) => i.type === ty));
      } catch (e) {
        setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      }
    },
    [api],
  );
  useEffect(() => {
    void load(type);
  }, [load, type]);

  const keys = [...new Set((items ?? []).map((i) => i.key))].sort();
  const rowsOf = (k: string) =>
    (items ?? []).filter((i) => i.key === k).sort((a, b) => b.version - a.version);

  function start(it: CatalogItem) {
    setIssues([]);
    setNotice(null);
    setFailure(null);
    setEdit({
      key: it.key,
      version: it.version,
      ko: it.i18n.ko,
      en: it.i18n.en,
      payload: it.payload == null ? "" : JSON.stringify(it.payload, null, 2),
      isNew: false,
      basePublished: !!it.publishedAt,
    });
  }

  async function save(publishAfter: boolean) {
    if (!edit) return;
    const errs: Issue[] = [];
    if (!KEY_RE.test(edit.key)) errs.push({ path: "/key", message: t("ops.catalog.badKey") });
    if (!edit.ko.trim()) errs.push({ path: "/i18n/ko", message: t("ops.catalog.required") });
    if (!edit.en.trim()) errs.push({ path: "/i18n/en", message: t("ops.catalog.required") });
    let payload: Json = null;
    if (edit.payload.trim()) {
      const p = parseDefinition(edit.payload);
      if (p.ok) payload = p.value;
      else errs.push({ path: "/payload", message: t("ops.modes.jsonError", { msg: p.message }) });
    }
    if (errs.length) {
      setIssues(errs);
      return;
    }
    const all = rowsOf(edit.key);
    const next = edit.basePublished ? (all[0]?.version ?? 0) + 1 : edit.version;
    setBusy(true);
    setIssues([]);
    setFailure(null);
    try {
      await api.saveCatalog(type, edit.key, next, {
        i18n: { ko: edit.ko.trim(), en: edit.en.trim() },
        payload,
      });
      if (publishAfter) await api.publishCatalog(type, edit.key, next);
      await load(type);
      setEdit({ ...edit, version: next, isNew: false, basePublished: publishAfter });
      setNotice(
        t(publishAfter ? "ops.catalog.published" : "ops.catalog.saved", { key: edit.key, v: next }),
      );
    } catch (e) {
      if (e instanceof ValidationError) setIssues(e.issues);
      else if (e instanceof Conflict) setFailure("HTTP-409");
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  async function act(fn: () => Promise<void>, msg: string) {
    setBusy(true);
    setFailure(null);
    try {
      await fn();
      await load(type);
      setNotice(msg);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  const err = (p: string) => issues.find((i) => i.path === p)?.message ?? null;

  return (
    <div className="pf-ops-catalog" data-testid="ops-catalog">
      <section className="pf-pane" aria-labelledby={`${id}-h`}>
        <h2 id={`${id}-h`} className="pf-h3">
          {t("ops.catalog.title")}
        </h2>
        <div
          className="pf-seg pf-seg--pill"
          role="tablist"
          aria-labelledby={`${id}-h`}
          data-testid="catalog-types"
        >
          {CATALOG_TYPES.map((ty) => (
            <button
              key={ty}
              type="button"
              role="tab"
              aria-selected={ty === type}
              className={`pf-ops-type${ty === type ? " pf-ops-type--on" : ""}`}
              data-type={ty}
              onClick={() => {
                setType(ty);
                setEdit(null);
                setNotice(null);
              }}
            >
              {t(`ops.catalog.type.${ty}`)} {counts[ty] ?? 0}
            </button>
          ))}
        </div>
      </section>

      <div className="pf-ops-split">
        <section className="pf-pane" aria-label={t(`ops.catalog.type.${type}`)} role="tabpanel">
          {items === null ? (
            <p className="pf-muted">{t("common.loading")}</p>
          ) : keys.length === 0 ? (
            <Empty label={t("ops.catalog.empty")} />
          ) : (
            <table className="pf-tbl" data-testid="catalog-table">
              <thead>
                <tr>
                  <th scope="col">{t("ops.catalog.colKey")}</th>
                  <th scope="col">{t("ops.catalog.colVersion")}</th>
                  <th scope="col">{t("ops.catalog.colKo")}</th>
                  <th scope="col">{t("ops.catalog.colState")}</th>
                  <th scope="col">
                    <span className="pf-sr-only">{t("teacher.nick.colAction")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.flatMap((k) =>
                  rowsOf(k).map((it) => {
                    const on = edit?.key === it.key && edit.version === it.version;
                    return (
                      <tr
                        key={`${k}@${it.version}`}
                        data-key={k}
                        data-version={it.version}
                        className={on ? "pf-tbl__row--on" : undefined}
                      >
                        <th scope="row">{k}</th>
                        <td>v{it.version}</td>
                        <td>{it.i18n.ko}</td>
                        <td>
                          <span
                            className={`pf-chip-static ${it.enabled ? "pf-chip-static--ok" : it.publishedAt ? "" : "pf-chip-static--run"}`}
                          >
                            {it.enabled
                              ? t("ops.catalog.stateLive")
                              : it.publishedAt
                                ? t("ops.catalog.stateOff")
                                : t("ops.catalog.stateDraft")}
                          </span>
                        </td>
                        <td className="pf-tbl__act">
                          <Button
                            variant="ghost"
                            className="pf-btn--sm"
                            aria-label={t("ops.catalog.editLabel", { key: k, v: it.version })}
                            data-testid="catalog-edit"
                            onClick={() => start(it)}
                          >
                            {t("teacher.works.edit")}
                          </Button>
                        </td>
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table>
          )}
          <Button
            variant="ghost"
            className="pf-btn--sm"
            data-testid="catalog-add"
            onClick={() => {
              setIssues([]);
              setNotice(null);
              setEdit({
                key: "",
                version: 1,
                ko: "",
                en: "",
                payload: "",
                isNew: true,
                basePublished: false,
              });
            }}
          >
            {t("ops.catalog.add")}
          </Button>
        </section>

        <section
          className="pf-pane pf-pane--hero"
          aria-labelledby={`${id}-ed`}
          data-testid="catalog-editor"
        >
          <h2 id={`${id}-ed`} className="pf-h3">
            {edit
              ? edit.isNew
                ? t("ops.catalog.newHead", { type: t(`ops.catalog.type.${type}`) })
                : edit.basePublished
                  ? t("ops.catalog.headPublished", {
                      key: edit.key,
                      v: edit.version,
                      next: rowsOf(edit.key)[0]!.version + 1,
                    })
                  : t("ops.catalog.headDraft", { key: edit.key, v: edit.version })
              : t("ops.catalog.pick")}
          </h2>
          {edit && (
            <>
              {edit.isNew && (
                <div className="pf-field-row">
                  <label className="pf-field-label" htmlFor={`${id}-key`}>
                    {t("ops.catalog.colKey")}
                  </label>
                  <input
                    id={`${id}-key`}
                    className="pf-input"
                    value={edit.key}
                    aria-invalid={err("/key") ? true : undefined}
                    data-testid="catalog-key"
                    onChange={(e) => setEdit({ ...edit, key: e.target.value })}
                  />
                  {err("/key") && <span className="pf-field-err">{err("/key")}</span>}
                </div>
              )}
              {(["ko", "en"] as const).map((l) => (
                <div key={l} className="pf-field-row">
                  <label className="pf-field-label" htmlFor={`${id}-${l}`}>
                    {t(`ops.catalog.i18n.${l}`)}
                  </label>
                  <input
                    id={`${id}-${l}`}
                    className="pf-input"
                    style={{ flex: 1 }}
                    value={edit[l]}
                    maxLength={80}
                    aria-invalid={err(`/i18n/${l}`) ? true : undefined}
                    data-testid={`catalog-${l}`}
                    onChange={(e) => setEdit({ ...edit, [l]: e.target.value })}
                  />
                  {err(`/i18n/${l}`) && <span className="pf-field-err">{err(`/i18n/${l}`)}</span>}
                </div>
              ))}
              <label className="pf-field-label" htmlFor={`${id}-payload`}>
                {t("ops.catalog.payload")}
              </label>
              <textarea
                id={`${id}-payload`}
                className="pf-input pf-input--code"
                style={{ minHeight: 90 }}
                value={edit.payload}
                spellCheck={false}
                aria-invalid={err("/payload") ? true : undefined}
                onChange={(e) => setEdit({ ...edit, payload: e.target.value })}
              />
              {err("/payload") && <span className="pf-field-err">{err("/payload")}</span>}
              {issues.length > 0 &&
                !issues.every((i) =>
                  ["/key", "/i18n/ko", "/i18n/en", "/payload"].includes(i.path),
                ) && (
                  <ErrorBanner
                    cause={issues.map((i) => `${i.path}: ${i.message}`).join(" · ")}
                    action={t("ops.config.invalidNext")}
                    code="HTTP-422"
                  />
                )}
              <div className="pf-row pf-row--wrap">
                <Button
                  variant="ghost"
                  className="pf-btn--sm"
                  disabled={busy}
                  data-testid="catalog-save"
                  onClick={() => void save(false)}
                >
                  {t("ops.catalog.saveDraft")}
                </Button>
                <Button
                  variant="accent"
                  className="pf-btn--sm"
                  disabled={busy}
                  data-testid="catalog-publish"
                  onClick={() => void save(true)}
                >
                  {t("ops.catalog.publish")}
                </Button>
                {!edit.isNew && (
                  <Button
                    variant="ghost"
                    className="pf-btn--sm pf-row__end"
                    disabled={busy || !rowsOf(edit.key).some((r) => r.enabled)}
                    data-testid="catalog-disable"
                    onClick={() =>
                      void act(
                        () => api.disableCatalog(type, edit.key),
                        t("ops.catalog.disabled", { key: edit.key }),
                      )
                    }
                  >
                    {t("ops.catalog.disable")}
                  </Button>
                )}
              </div>
            </>
          )}
          {notice && (
            <p className="pf-note" role="status" data-testid="catalog-notice">
              {notice}
            </p>
          )}
          {failure && (
            <ErrorBanner
              cause={t("ops.common.saveFailed")}
              action={t("ops.common.retry")}
              code={failure}
            />
          )}
          <p className="pf-note">{t("ops.catalog.note")}</p>
          {edit && !edit.isNew && (
            <p className="pf-note">
              {t("ops.catalog.publishedAt", {
                at: fmtDate(
                  rowsOf(edit.key).find((r) => r.version === edit.version)?.publishedAt ?? null,
                ),
              })}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
